import {createStyles, Switch} from '@mantine/core';
import React, {useState} from 'react';

import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import StepTypeMenu from 'pipelines/components/StepTypeMenu';
import getTestProps from 'test/lib/cypress/getTestProps';
import PlainSearchInput, {BorderRadius, ClearType} from 'ui-library/PlainSearchInput';

const useStyles = createStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  searchBar: {
    flexShrink: 0,
    paddingBottom: '12px',
  },
  searchInput: {
    width: '100%',
  },
  menuWrapper: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
  },
  footer: {
    flexShrink: 0,
    padding: '8px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
});

interface StepTypeSelectorProps {
  selectedStepType: PipelineStepType | null;
  onSelectStepType: (stepType: PipelineStepType | null) => void;
  /**
   * Determines which step types are shown. `StepTypeSelector` performs no gating of its own, so callers
   * must compose `useCheckStepTypeEnabledForUser` into this filter to hide step types the user cannot
   * create or run.
   */
  filterStepTypes: (stepType: PipelineStepType) => boolean;
}

/**
 * Shared step type selection UI used by both AddStepRightSidebar, AddSourceStepRightSidebar and
 * ReplaceSourceStepRightSidebar.
 *
 * Renders a search bar, a StepTypeMenu, and a descriptions toggle.
 *
 * Each caller manages which step types are available through `filterStepTypes`.
 */
export default function StepTypeSelector({
  selectedStepType,
  onSelectStepType,
  filterStepTypes,
}: StepTypeSelectorProps): JSX.Element {
  const {classes} = useStyles();
  const [showDescriptions, setShowDescriptions] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  return (
    <div className={classes.container} {...getTestProps({component: 'StepTypeSelector', element: 'root'})}>
      <div className={classes.searchBar}>
        <PlainSearchInput
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
          }}
          onEscape={() => {
            setSearchQuery('');
          }}
          clearType={ClearType.X}
          borderRadius={BorderRadius.LARGE}
          className={classes.searchInput}
        />
      </div>
      <div className={classes.menuWrapper}>
        <StepTypeMenu
          filterStepTypes={filterStepTypes}
          selectedStepType={selectedStepType}
          onSelectStepType={onSelectStepType}
          showDescription={showDescriptions}
          searchQuery={searchQuery}
        />
        <div className={classes.footer}>
          <Switch
            label="Step descriptions"
            checked={showDescriptions}
            onChange={(e) => {
              setShowDescriptions(e.currentTarget.checked);
            }}
            styles={{
              root: {justifyContent: 'flex-end'},
              body: {alignItems: 'center'},
            }}
          />
        </div>
      </div>
    </div>
  );
}
